import React, { ReactNode } from 'react'
import MediaQuery from 'react-responsive'

import { AcornsIcon } from '@acorns/icons'
import {
  Heading,
  Subheading,
  TooltipV2 as Tooltip,
  TooltipV2Props as TooltipProps,
} from '@acorns/web-components'
import styled, { css } from 'styled-components'

import themeValues, { style } from 'theme'

export type StatsProps = {
  value: string
  description: string
  tooltip?: TooltipProps
  icon?: ReactNode
  testId?: string
}

const StatsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  @media screen and (max-width: ${style((theme) => theme.breakpoints.sm)}px) {
    align-items: flex-start;
    background-color: ${style((theme) => theme.colors.ivoryLight)};
    padding: 20px;
    border-radius: 10px;
    min-width: unset;

    &:nth-last-child(1):nth-child(odd) {
      grid-column: auto / span 2;
    }
  }
`

const TooltipIconContainer = styled.div`
  all: unset;
  background-color: ${style((theme) => theme.colors.stoneLight)};
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`

const DescriptionContainer = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
`

const StyledHeading = styled(Heading)`
  line-height: 24px;
`

const StyledSubheading = styled(Subheading)<{ hasTooltip?: boolean }>`
  ${(props) =>
    props.hasTooltip &&
    css`
      margin-right: 5px;
    `}

  line-height: 16px;
  font-weight: 400;
`

const InfoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  @media screen and (max-width: ${style((theme) => theme.breakpoints.sm)}px) {
    align-items: flex-start;
    flex-direction: column-reverse;
  }
`

const IconContainer = styled.div`
  margin-bottom: 12px;
`

const StyledTooltipIcon = styled(AcornsIcon)`
  margin-left: 1px;
`

// TODO: We need this component because of quirks mode, remove when ready.
const TooltipContainer = styled.div`
  position: relative;
`

const StyledTooltip = styled(Tooltip)`
  > div:last-child {
    white-space: pre-wrap;

    @media screen and (max-width: ${(props) => props.theme.breakpoints.sm}px) {
      max-width: 250px;
    }
  }
`

export const Stats = ({
  value,
  description,
  tooltip,
  icon,
  testId,
}: StatsProps) => (
  <StatsContainer>
    <MediaQuery maxWidth={themeValues?.breakpoints.sm}>
      <IconContainer>{icon}</IconContainer>
    </MediaQuery>
    <InfoContainer>
      <StyledHeading data-testid={`${testId}_value`}>{value}</StyledHeading>
      <DescriptionContainer>
        <StyledSubheading
          data-testid={`${testId}_description`}
          hasTooltip={!!tooltip}
        >
          {description}
        </StyledSubheading>
        {tooltip && (
          <TooltipContainer>
            <StyledTooltip
              {...tooltip}
              testId={`${testId}_tooltip_content`}
              trigger={'click'}
            >
              <TooltipIconContainer>
                <StyledTooltipIcon
                  icon={AcornsIcon.Icon?.Tooltip}
                  width={3}
                  height={8}
                />
              </TooltipIconContainer>
            </StyledTooltip>
          </TooltipContainer>
        )}
      </DescriptionContainer>
    </InfoContainer>
  </StatsContainer>
)
